/**
 * Admin Invoices Management Page
 * View and manage all client invoices
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  Search,
  Filter,
  Download,
  Edit,
  Trash2,
  DollarSign,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  Plus,
  RefreshCw,
  Send,
} from 'lucide-react';

interface Invoice {
  id: number;
  invoiceNumber: string;
  clientId: number;
  clientName: string;
  clientEmail: string;
  status: 'draft' | 'unpaid' | 'paid' | 'cancelled' | 'refunded';
  total: number;
  balance: number;
  invoiceDate: string;
  dueDate: string;
  paidDate?: string;
}

const statusConfig = {
  draft: {
    label: 'Draft',
    icon: FileText,
    color: 'text-gray-400',
    bgColor: 'bg-gray-900/20',
  },
  unpaid: {
    label: 'Unpaid',
    icon: Clock,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-900/20',
  },
  paid: {
    label: 'Paid',
    icon: CheckCircle,
    color: 'text-green-400',
    bgColor: 'bg-green-900/20',
  },
  cancelled: {
    label: 'Cancelled',
    icon: XCircle,
    color: 'text-gray-400',
    bgColor: 'bg-gray-900/20',
  },
  refunded: {
    label: 'Refunded',
    icon: XCircle,
    color: 'text-purple-400',
    bgColor: 'bg-purple-900/20',
  },
};

const InvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `/api/v1/admin/invoices?status=${filterStatus === 'all' ? '' : filterStatus}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setInvoices(data.data.invoices);
      }
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const searchMatch =
      searchQuery === '' ||
      invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.clientEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.id.toString().includes(searchQuery);

    return searchMatch;
  });

  const isOverdue = (invoice: Invoice) => {
    if (invoice.status !== 'unpaid') return false;
    return new Date(invoice.dueDate) < new Date();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const totalRevenue = invoices
    .filter((inv) => inv.status === 'paid')
    .reduce((sum, inv) => sum + inv.total, 0);

  const totalUnpaid = invoices
    .filter((inv) => inv.status === 'unpaid')
    .reduce((sum, inv) => sum + inv.balance, 0);

  const totalOverdue = invoices
    .filter((inv) => isOverdue(inv))
    .reduce((sum, inv) => sum + inv.balance, 0);

  const handleSendReminder = async (invoiceId: number) => {
    if (!confirm('Send payment reminder to client?')) return;

    try {
      await fetch(`/api/v1/admin/invoices/${invoiceId}/send-reminder`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      alert('Reminder sent successfully');
    } catch (error) {
      console.error('Failed to send reminder:', error);
      alert('Failed to send reminder');
    }
  };

  const handleDeleteInvoice = async (invoiceId: number) => {
    if (!confirm('Are you sure you want to delete this invoice? This action cannot be undone.'))
      return;

    try {
      await fetch(`/api/v1/admin/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      loadInvoices();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      alert('Failed to delete invoice');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FileText className="w-8 h-8" />
            Invoices Management
          </h1>
          <p className="text-gray-400 mt-2">Manage client invoices and payments</p>
        </div>
        <Link
          to="/admin/invoices/create"
          className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 flex items-center gap-2 font-medium shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40"
        >
          <Plus className="w-5 h-5" />
          Create Invoice
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-fade-in">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-orange-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Invoices</p>
              <p className="text-2xl font-bold text-white mt-1">{invoices.length}</p>
            </div>
            <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-orange-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-green-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Revenue</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {formatPrice(totalRevenue)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-yellow-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Unpaid Amount</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">
                {formatPrice(totalUnpaid)}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-red-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Overdue Amount</p>
              <p className="text-2xl font-bold text-red-400 mt-1">
                {formatPrice(totalOverdue)}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by invoice number, client name, or email..."
            className="w-full pl-10 pr-4 py-3 bg-slate-800/80 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-colors"
          />
        </div>

        <div className="flex gap-2">
          {['all', 'draft', 'unpaid', 'paid', 'overdue', 'cancelled'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-3 rounded-lg font-medium transition-all duration-300 whitespace-nowrap ${
                filterStatus === status
                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                  : 'bg-slate-800/80 text-slate-400 hover:bg-slate-700/50 hover:text-white border border-slate-700/50'
              }`}
            >
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Invoices Table */}
      {loading ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
            <span className="ml-3 text-slate-400">Loading invoices...</span>
          </div>
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50 text-center">
          <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Invoices Found</h3>
          <p className="text-gray-400">
            {searchQuery ? 'No invoices match your search.' : 'No invoices in the system yet.'}
          </p>
        </div>
      ) : (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-4 text-slate-300 font-medium">Invoice</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Client</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Date</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Due Date</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Amount</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Status</th>
                  <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filteredInvoices.map((invoice) => {
                  const overdue = isOverdue(invoice);
                  const status = overdue ? 'overdue' : invoice.status;
                  const statusInfo =
                    statusConfig[invoice.status as keyof typeof statusConfig];
                  const StatusIcon = statusInfo.icon;

                  return (
                    <tr key={invoice.id} className="hover:bg-slate-700/30">
                      <td className="p-4">
                        <Link
                          to={`/admin/invoices/${invoice.id}`}
                          className="text-orange-400 hover:text-orange-300 font-medium font-mono"
                        >
                          {invoice.invoiceNumber}
                        </Link>
                      </td>
                      <td className="p-4">
                        <Link
                          to={`/admin/clients/${invoice.clientId}`}
                          className="text-white hover:text-orange-400"
                        >
                          {invoice.clientName}
                        </Link>
                        <div className="text-xs text-gray-400">{invoice.clientEmail}</div>
                      </td>
                      <td className="p-4 text-gray-300">{formatDate(invoice.invoiceDate)}</td>
                      <td className="p-4">
                        <span className={overdue ? 'text-red-400 font-medium' : 'text-gray-300'}>
                          {formatDate(invoice.dueDate)}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-white font-medium">{formatPrice(invoice.total)}</span>
                        {invoice.balance > 0 && invoice.balance < invoice.total && (
                          <div className="text-xs text-gray-400">
                            Balance: {formatPrice(invoice.balance)}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                            overdue ? 'bg-red-900/20 text-red-400' : statusInfo.bgColor
                          } ${overdue ? 'text-red-400' : statusInfo.color}`}
                        >
                          <StatusIcon className="w-4 h-4" />
                          {overdue ? 'Overdue' : statusInfo.label}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/admin/invoices/${invoice.id}`}
                            className="p-2 text-orange-400 hover:bg-slate-700/50 rounded transition-colors"
                            title="View/Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Link>
                          <button
                            className="p-2 text-gray-400 hover:bg-slate-700/50 rounded transition-colors"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          {invoice.status === 'unpaid' && (
                            <button
                              onClick={() => handleSendReminder(invoice.id)}
                              className="p-2 text-yellow-400 hover:bg-slate-700/50 rounded transition-colors"
                              title="Send Reminder"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          )}
                          {invoice.status === 'draft' && (
                            <button
                              onClick={() => handleDeleteInvoice(invoice.id)}
                              className="p-2 text-red-400 hover:bg-slate-700/50 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
